/**
 * Headless CKFinder QuickUpload helper for PPTX files (no CKFinder UI).
 *
 * Layout: all staging uploads share ONE folder and get a unique filename suffix,
 * so there are no per-upload folders to accumulate. The connector maps the
 * Resources type to `<basedir>/<path>resources`, and the AwsS3 backend root is
 * `content`, so the final S3 key is:
 *   content/<tenantId>/elearnings/pptx-import/pages/resources/<uniqueFileName>
 * The backend copies it to the canonical `.../elearnings/<id>/source.pptx`, so the
 * unique suffix is naturally dropped on import, and the backend deletes just this
 * file afterwards (never the shared folder).
 *
 * Path policy: `elearnings/pptx-import/pages/` satisfies the connector's
 * ElaCustomCKFCAuth regex `^(elearnings|quizzes|themes)/([\w-]+)/pages`.
 *
 * Auth: the CKFC cookie (GET /signed-cookies/ckfinder-token/generate, polled by
 * App.tsx) is sent automatically on same-origin requests; `credentials: 'include'`
 * makes the intent explicit.
 *
 * CSRF: CKFinder 3 uses stateless double-submit — a `ckCsrfToken` cookie compared
 * to the same value sent as a form field. We generate one, set the cookie, and
 * include it in the FormData. (Needs real-environment confirmation.)
 */

export interface CkfinderUploadResult {
  /** Full S3 key of the uploaded file, for the backend import call. */
  s3Key: string;
  /** Original (un-suffixed) file name, for display. */
  fileName: string;
}

/**
 * User-facing message for both oversize-upload failure modes: an ingress/proxy 413
 * (a body-size limit below the app's PHP max), and a non-JSON connector response
 * (PHP post_max_size exceeded, so the POST body is discarded and the connector
 * returns no JSON). Names the 30MB product limit so the user knows the ceiling
 * and can act (reduce size or split into multiple modules).
 */
export const PPTX_TOO_LARGE_MESSAGE =
  'This file is larger than the 30MB limit. Please reduce the file size or split it into multiple modules and try again.';

/** Single shared staging folder — matches the ElaCustomCKFCAuth `[\w-]+/pages` regex. */
const STAGING_PATH = 'elearnings/pptx-import/pages/';

/**
 * Create-with-AI context files (LMS2-5164). A separate folder from the PPTX one so the
 * two can be expired on different schedules, and still shaped to satisfy the same
 * connector path policy.
 */
const CONTEXT_STAGING_PATH = 'elearnings/ai-context/pages/';

// URL-safe random id via crypto, strong enough for the CSRF token below.
function randomId(length: number): string {
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += alphabet[bytes[i] % alphabet.length];
  }
  return out;
}

const CSRF_COOKIE = 'ckCsrfToken';
/** CKFinder 3 mints 40-char tokens; anything much shorter is not one of ours or its. */
const CSRF_TOKEN_MIN_LENGTH = 32;
/** CKFinder 3 error 207: the upload succeeded but the stored name differs from the one sent. */
const CKFINDER_FILE_RENAMED = 207;

/**
 * One CSRF token per page, shared by every upload. The double-submit check compares the
 * form field against the cookie, and the cookie is a single value: two uploads running
 * in parallel (the admin picks two context files at once) that each minted their own
 * token would overwrite each other's cookie, and whichever request the connector read
 * second would fail with "Invalid CSRF token". Reuse the cookie already set (by an
 * earlier upload, or CKFinder's own UI) and only mint one when there is none.
 */
function csrfToken(): string {
  try {
    const existing = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${CSRF_COOKIE}=`))
      ?.slice(CSRF_COOKIE.length + 1);
    // A cookie we did not mint (another script, a stale value) could be one the
    // connector rejects; on anything that does not look like a token, mint a fresh one.
    if (existing && existing.length >= CSRF_TOKEN_MIN_LENGTH) {
      return existing;
    }
    // CKFinder 3 generates 40-char tokens and its validator may reject shorter ones.
    const token = randomId(40);
    document.cookie = `${CSRF_COOKIE}=${token};path=/`;
    return token;
  } catch {
    // non-browser environment — the cookie is best-effort, the field still goes out
    return randomId(40);
  }
}

/** Splits a filename into [base, ext]; ext defaults to 'pptx'. */
function splitName(
  fileName: string,
  fallbackExtension: string,
): [string, string] {
  const dot = fileName.lastIndexOf('.');
  return dot > 0
    ? [fileName.slice(0, dot), fileName.slice(dot + 1)]
    : [fileName, fallbackExtension];
}

const uploadToCkfinder = async (
  file: File,
  {
    tenantId,
    connectorPath,
    stagingPath,
    fallbackExtension,
  }: {
    tenantId: string;
    connectorPath: string;
    stagingPath: string;
    fallbackExtension: string;
  },
): Promise<CkfinderUploadResult> => {
  // Unique upload name "<base>_<random>.<ext>" so the shared folder never collides
  // (CKFinder overwriteOnUpload=false would otherwise auto-rename to deck(1).pptx).
  const [rawBase, ext] = splitName(file.name, fallbackExtension);
  // CKFinder sanitises names it does not like (spaces, extra dots — "Policy v1.4.pdf")
  // and answers with error 207 FILE_RENAMED even though the upload succeeded. Send a
  // name it will keep, so a rename can only ever mean a real collision.
  const base =
    rawBase
      .split(/[^A-Za-z0-9_-]+/)
      .join('_')
      .replace(/^_+/, '')
      .replace(/_+$/, '') || 'file';
  const uploadFileName = `${base}_${randomId(21)}.${ext}`;

  // Stateless CSRF double-submit: the same value as a cookie and as a form field.
  const formData = new FormData();
  formData.append('upload', file, uploadFileName);
  formData.append(CSRF_COOKIE, csrfToken());

  const url = new URL(connectorPath);
  url.searchParams.set('command', 'QuickUpload');
  url.searchParams.set('type', 'Resources');
  url.searchParams.set('currentFolder', '/');
  url.searchParams.set('basedir', tenantId);
  url.searchParams.set('path', stagingPath);
  url.searchParams.set('responseType', 'json');

  const response = await fetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: formData,
  });

  if (!response.ok) {
    // A proxy/ingress body-size limit (below the app's PHP max) rejects with 413.
    if (response.status === 413) {
      throw new Error(PPTX_TOO_LARGE_MESSAGE);
    }
    throw new Error(
      `CKFinder upload failed with HTTP ${response.status}: ${response.statusText}`,
    );
  }

  const rawBody = await response.text();
  let json: any;
  try {
    json = JSON.parse(rawBody);
  } catch {
    // An empty/whitespace body is the post_max_size signature: PHP discards the
    // oversize POST and the connector emits nothing. A non-empty non-JSON body
    // (e.g. an HTML error page) is a different failure and must not be mislabelled
    // as oversize.
    if (rawBody.trim() === '') {
      throw new Error(PPTX_TOO_LARGE_MESSAGE);
    }
    // Customer-facing: the raw connector/proxy failure is not actionable to the user.
    throw new Error('Upload failed unexpectedly. Please try again.');
  }

  // CKFinder 3 error response: { error: { number, message } }. FILE_RENAMED is a
  // warning on a successful upload, and the response still carries `uploaded: 1` and
  // the final `fileName`, which is all the key derivation below needs.
  if (json?.error && json.error.number !== CKFINDER_FILE_RENAMED) {
    throw new Error(
      `CKFinder error ${json.error.number}: ${json.error.message}`,
    );
  }

  // CKFinder 3 success response: { uploaded: 1, fileName, url }
  if (!json?.uploaded || !json?.fileName) {
    throw new Error(`Unexpected CKFinder response: ${JSON.stringify(json)}`);
  }

  const returnedFileName: string = json.fileName;

  // Derive the S3 key from the returned (actually-stored) file name. Layout:
  // adapter root "content" + directory "<basedir>/<path>resources" + file name.
  const s3Key = `content/${tenantId}/${stagingPath}resources/${returnedFileName}`;

  // Display the original name, not the suffixed one.
  return { s3Key, fileName: file.name };
};

export const uploadPptxToCkfinder = (
  file: File,
  options: { tenantId: string; connectorPath: string },
): Promise<CkfinderUploadResult> =>
  uploadToCkfinder(file, {
    ...options,
    stagingPath: STAGING_PATH,
    fallbackExtension: 'pptx',
  });

/**
 * Stages one Create-with-AI context file (PDF, PPTX or DOCX).
 *
 * The bytes cross the wire once, here, instead of on every Create click: LMS2-5164's
 * Scope is "head-check the staging key, copy to the canonical path, delete staging", and
 * the API deletes this object once it has copied it.
 */
export const uploadContextFileToCkfinder = (
  file: File,
  options: { tenantId: string; connectorPath: string },
): Promise<CkfinderUploadResult> =>
  uploadToCkfinder(file, {
    ...options,
    stagingPath: CONTEXT_STAGING_PATH,
    fallbackExtension: 'pdf',
  });
